import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

interface PageContent {
  id: string;
  page_key: string;
  section_key: string;
  language_code: string;
  title: string | null;
  subtitle: string | null;
  content: string | null;
  button_text: string | null;
  button_link: string | null;
  image_url: string | null;
  extra_data: Record<string, unknown> | null;
  sort_order: number;
  is_active: boolean;
}

export const usePageContent = (pageKey: string, sectionKey?: string) => {
  const { currentLanguage } = useLanguage();

  return useQuery({
    queryKey: ["page-content", pageKey, sectionKey, currentLanguage],
    queryFn: async () => {
      let query = supabase
        .from("page_content")
        .select("*")
        .eq("page_key", pageKey)
        .eq("language_code", currentLanguage)
        .eq("is_active", true)
        .order("sort_order");

      if (sectionKey) {
        query = query.eq("section_key", sectionKey);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PageContent[];
    },
  });
};

export const useSection = (pageKey: string, sectionKey: string) => {
  const { data, isLoading, error } = usePageContent(pageKey, sectionKey);
  return {
    section: data?.[0] || null,
    isLoading,
    error,
  };
};

// Hook to fetch multiple sections at once for a page
export const usePageSections = (pageKey: string) => {
  const { currentLanguage } = useLanguage();

  return useQuery({
    queryKey: ["page-sections", pageKey, currentLanguage],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_content")
        .select("*")
        .eq("page_key", pageKey)
        .eq("language_code", currentLanguage)
        .eq("is_active", true)
        .order("sort_order");

      if (error) throw error;

      // Convert to a map for easy access by section_key
      const sectionsMap: Record<string, PageContent> = {};
      (data as PageContent[]).forEach((item) => {
        sectionsMap[item.section_key] = item;
      });

      return sectionsMap;
    },
  });
};

// Hook to get global content (footer, buttons, etc.)
export const useGlobalContent = (sectionKey: string) => {
  return useSection("global", sectionKey);
};

// Helper to safely parse extra_data items array
export const getExtraDataItems = <T>(section: PageContent | null): T[] => {
  if (!section?.extra_data) return [];
  const items = (section.extra_data as Record<string, unknown>).items;
  if (Array.isArray(items)) {
    return items as T[];
  }
  return [];
};

// Helper to get a specific field from extra_data
export const getExtraDataField = <T>(
  section: PageContent | null,
  field: string,
  defaultValue: T
): T => {
  if (!section?.extra_data) return defaultValue;
  const value = (section.extra_data as Record<string, unknown>)[field];
  return value !== undefined ? (value as T) : defaultValue;
};

export type { PageContent };
